import React, { useState, useEffect, useCallback } from 'react';
import './Navigation.css';

const NAV_ITEMS = [
  { id: 'education', label: 'Education' },
  { id: 'work', label: 'Work' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [compactNav, setCompactNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onMq = () => {
      const next = mq.matches;
      setCompactNav(next);
      if (!next) setMenuOpen(false);
    };
    onMq();
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    setMenuOpen(false);
  }, []);

  return (
    <nav
      className={`navigation ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'nav-open' : ''}`}
      aria-label="Main"
    >
      <div className="nav-container">
        <button
          type="button"
          className="nav-logo"
          onClick={() => scrollToSection('hero')}
          aria-label="Go to top"
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/mb_logo.png`}
            alt=""
            className="nav-logo-img"
            width={50}
            height={50}
            decoding="async"
          />
        </button>
        <button
          type="button"
          className="nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="nav-menu-panel"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span aria-hidden />
          <span aria-hidden />
          <span aria-hidden />
        </button>
        <ul
          id="nav-menu-panel"
          className={`nav-links ${menuOpen ? 'nav-links--open' : ''}`}
          {...(compactNav && !menuOpen ? { inert: '' } : {})}
        >
          {NAV_ITEMS.map(({ id, label }) => (
            <li key={id}>
              <button type="button" onClick={() => scrollToSection(id)}>
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        className="nav-backdrop"
        aria-label="Close menu"
        aria-hidden={!menuOpen}
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />
    </nav>
  );
};

export default Navigation;
